/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { faFileMagnifyingGlass } from "@fortawesome/pro-duotone-svg-icons";
import { Virtuoso } from "react-virtuoso";
import Colors, { shadeColor } from "styles/colors";
import { ReportDTO } from "pages/reports/bi-dashboard-next/models/ReportDTO";
import { ReportsListViewRow } from "pages/reports/bi-dashboard-next/components/organisms/Reports.ListViewRow.organism";
import { ReportsGridViewCard } from "pages/reports/bi-dashboard-next/components/organisms/ReportsGridViewCard.organism";
import { ReportsLoadingSpinner } from "pages/reports/bi-dashboard-next/components/molecules/Reports.LoadingSpinner.molecule";
import { ReportTabType } from "pages/reports/bi-dashboard-next/models/ReportTabType";

interface ReportsContentProps {
  viewMode: string;
  fade: boolean;
  isLoading: boolean;
  reports: ReportDTO[];
  loadMore: () => void;
  isFetchingNextPage: boolean;
  onTagClick: (tagName: string) => void;
  refetch?: () => void;
  toggleFavorite?: (reportId: number, favorited: boolean) => void;
  isTogglingFavorite?: Record<number, boolean>;
  currentSection?:
    | "favorites"
    | "recentlyOpened"
    | "scheduled"
    | "openedThisWeek"
    | "all";
  activeTab?: ReportTabType;
}

export const ReportsContent: React.FC<ReportsContentProps> = ({
  viewMode,
  fade,
  isLoading,
  reports,
  loadMore,
  isFetchingNextPage,
  onTagClick,
  refetch,
  toggleFavorite,
  isTogglingFavorite,
  currentSection = "all",
  activeTab,
}) => {
  const { t } = useTranslation("reports");

  return (
    <div
      css={{
        display: "flex",
        transition: "all .5s ease-in-out",
        flexDirection: "column",
        backgroundColor:
          viewMode === "row" ? Colors.biDashboardView.CARD_BACKGROUND : "none",
        boxShadow:
          viewMode === "row"
            ? Colors.biDashboardView.LEFT_NAVBAR_BOX_SHADOW
            : "none",
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        "::-webkit-scrollbar": {
          width: 6,
          background: Colors.biDashboardView.SCROLLBAR_BACKGROUND,
        },
        "::-webkit-scrollbar-thumb": {
          background: Colors.biDashboardView.SCROLLBAR_THUMB,
          borderRadius: 6,
        },
        "::-webkit-scrollbar-thumb:hover": {
          background: Colors.biDashboardView.SCROLLBAR_THUMB_HOVER,
        },
        "::-webkit-scrollbar-corner": {
          background: "transparent",
        },
        "::-webkit-scrollbar-button": {
          display: "none",
          height: 0,
        },
      }}
    >
      <div
        key={viewMode}
        css={{
          transition: "opacity 0.2s cubic-bezier(.4,0,.2,1)",
          opacity: fade ? 0 : 1,
          height: "100%",
          width: "100%",
        }}
      >
        {isLoading ? (
          <ReportsLoadingSpinner message={t("Loading reports...")} />
        ) : reports.length === 0 ? (
          <div
            data-qa="no-reports-message"
            css={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              padding: 20,
              textAlign: "center",
              color: Colors.biDashboardView.NO_REPORTS_TEXT,
              fontSize: 16,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <FontAwesomeIcon
              icon={faFileMagnifyingGlass}
              css={{
                position: "absolute",
                fontSize: 300,
                opacity: 0.1,
                color: "#666666",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 0,
              }}
              data-qa="no-reports-watermark"
            />
            <div css={{ position: "relative", zIndex: 1 }}>
              {activeTab === ReportTabType.MY ? (
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "20px",
                  }}
                >
                  <div>{t("You have not created any reports yet.")}</div>
                  <button
                    type="button"
                    onClick={() => (window.location.href = "/report_builder")}
                    css={{
                      padding: "10px 20px",
                      backgroundColor: Colors.biDashboardView.FOCUS_COLOR,
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      transition: "background-color 0.2s",
                      "&:hover": {
                        backgroundColor: shadeColor(
                          Colors.biDashboardView.FOCUS_COLOR,
                          -0.1,
                        ),
                      },
                    }}
                    data-qa="create-report-button"
                  >
                    {t("Create Report")}
                  </button>
                </div>
              ) : currentSection === "favorites" ? (
                t(
                  "You don't have any favorites. You can add favorites by clicking on the star from the 'All' view.",
                )
              ) : currentSection === "recentlyOpened" ? (
                t("No reports have been added in the last two weeks.")
              ) : currentSection === "scheduled" ? (
                t("You haven't scheduled any reports for email.")
              ) : currentSection === "openedThisWeek" ? (
                t("You haven't opened any reports this week.")
              ) : (
                t("No reports match the search criteria.")
              )}
            </div>
          </div>
        ) : viewMode === "row" ? (
          <Virtuoso
            style={{ height: "100%" }}
            data={reports}
            endReached={loadMore}
            overscan={200}
            itemContent={(index: number, report: ReportDTO) => (
              <ReportsListViewRow
                key={`ReportListView-${report.id}`}
                reportId={report.id}
                reportNumber={report.reportNumber}
                title={report.name}
                description={report.details}
                tags={report.tags}
                onTagClick={onTagClick}
                className={index % 2 === 1 ? "alt-row" : ""}
                favorited={report.favorited}
                onFavoriteToggle={toggleFavorite}
                isTogglingFavorite={isTogglingFavorite?.[report.id]}
                emailScheduleCount={report.emailScheduleCount}
                css={{
                  "&.alt-row": {
                    background: "#d0e8ec !important",
                  },
                  "&.alt-row:hover": {
                    background: "#b8dbe1 !important",
                  },
                  "&.alt-row .refresh-info span": {
                    color: "#5a8b96 !important",
                  },
                }}
              />
            )}
            components={{
              Footer: () =>
                isFetchingNextPage ? (
                  <ReportsLoadingSpinner
                    message={t("Loading more reports...")}
                  />
                ) : null,
            }}
          />
        ) : (
          <div
            css={{
              height: "100%",
              overflowY: "auto",
              padding: "0 10px 0 0",
            }}
            onScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
              if (scrollHeight - scrollTop <= clientHeight * 1.5) {
                loadMore();
              }
            }}
          >
            <div
              css={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gridGap: 10,
                paddingBottom: 20,
              }}
            >
              {reports.map((report: ReportDTO) => (
                <ReportsGridViewCard
                  key={`ReportGridView-${report.id}`}
                  reportId={report.id}
                  reportNumber={report.reportNumber}
                  title={report.name}
                  tags={report.tags}
                  description={report.details}
                  onTagClick={onTagClick}
                  favorited={report.favorited}
                  onFavoriteToggle={toggleFavorite}
                  isTogglingFavorite={isTogglingFavorite?.[report.id]}
                  emailScheduleCount={report.emailScheduleCount}
                />
              ))}
            </div>
            {isFetchingNextPage && (
              <div
                css={{
                  padding: 20,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FontAwesomeIcon
                  icon={faSpinner}
                  css={{
                    marginRight: 8,
                    animation: "spin 1.5s linear infinite",
                    color: Colors.biDashboardView.FOCUS_COLOR,
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
                <span css={{ color: "#999" }}>
                  {t("Loading more reports...")}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
